import React, { useState, useEffect } from 'react';
import { ArrowRight, MapPin, Users, Calendar, DollarSign, CheckCircle } from 'lucide-react';
import { DetailBox } from './DetailBox';
import { cn } from '@/lib/utils';
import { proposalTokens } from './tokens';
import './proposal.css';

interface ProposalMeta {
  event_name: string;
  client_name: string;
  dates?: string;
  budget_range?: string;
  group_size?: string;
  objectives?: string[];
}

interface Destination {
  city: string;
  country: string;
}

interface CoverPageProps {
  meta: ProposalMeta;
  destinations?: Destination[];
  heroImages?: string[];
  onEnter: () => void;
}

/**
 * CoverPage component - Full-screen introduction page for proposals
 * Adapted from template4 Intro component, following UI/UX guidelines
 */
export function CoverPage({ meta, destinations = [], heroImages = [], onEnter }: CoverPageProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Use provided hero images or fallback to prototype's 3 images
  const images = heroImages.length > 0 ? heroImages : [
    'https://images.unsplash.com/photo-1585208798174-6cedd86e019a?w=1200&q=80', // Historic Alfama District
    'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?w=1200&q=80', // Iconic Yellow Tram
    'https://images.unsplash.com/photo-1536663815808-535e2280d2c2?w=1200&q=80', // Belém Tower
  ];

  // Cycle through images with crossfade
  useEffect(() => {
    if (images.length <= 1) return;
    
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
        setIsTransitioning(false);
      }, 500); // Half of transition duration
    }, 7000); // Change every 7 seconds

    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <div className="proposal-container relative min-h-screen w-full overflow-y-auto bg-black">
      {/* Background Image Slideshow */}
      <div className="fixed inset-0 z-0">
        {images.map((image, index) => (
          <div
            key={index}
            className={cn(
              "absolute inset-0 transition-opacity duration-1000",
              index === currentImageIndex && !isTransitioning
                ? 'opacity-100'
                : 'opacity-0'
            )}
          >
            <img
              src={image}
              alt={`Destination ${index + 1}`}
              className="w-full h-full object-cover scale-105"
            />
          </div>
        ))}
      </div>

      {/* Dark Overlay */}
      <div className="fixed inset-0 z-[1] bg-black/70" />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 py-12 sm:py-8 text-center pb-20 sm:pb-24">
        {/* Logo Section */}
        <div className="mb-8 sm:mb-12 flex flex-col md:flex-row items-center gap-6 sm:gap-8">
          {/* Moniker Logo */}
          <div className="flex items-center gap-2 sm:gap-3">
            <img 
              src="/images/logo_moniker_white.png" 
              alt="Moniker" 
              className="h-8 sm:h-10 w-auto" 
            />
            <span className="text-white font-bold text-xl sm:text-2xl tracking-tight" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
              MONIKER
            </span>
          </div>

          {/* Divider */}
          <div className="hidden md:block w-px h-12 bg-white/20" />
          <div className="md:hidden w-24 h-px bg-white/20" />

          {/* Client Logo Placeholder */}
          {meta.client_name && (
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center border backdrop-blur-sm" style={{ 
                backgroundColor: 'rgba(239, 237, 231, 0.2)', // cream/20 - softer blurrier background
                borderColor: 'rgba(239, 237, 231, 0.3)', // cream/30
              }}>
                <span className="font-bold text-lg sm:text-xl" style={{ color: proposalTokens.colors.cream }}>
                  {meta.client_name.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="font-medium text-sm sm:text-base" style={{ color: `${proposalTokens.colors.cream}cc` }}>
                {meta.client_name}
              </span>
            </div>
          )}
        </div>

        {/* Title Section */}
        <div className="mb-6 sm:mb-8">
          <p className="proposal-text-gold uppercase tracking-[0.3em] text-xs sm:text-sm font-medium mb-3 sm:mb-4" style={{ color: proposalTokens.colors.gold }}>
            Proposal For
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-serif font-medium text-white mb-8 sm:mb-12 leading-tight px-2" style={{ fontFamily: proposalTokens.typography.fontFamily.serif.join(', ') }}>
            {meta.event_name}
          </h1>
          
          {/* Key Details Grid - Always show if we have at least one detail */}
          {(meta.dates || meta.budget_range || meta.group_size || meta.objectives?.[0]) ? (() => {
            // Count how many boxes we have
            const boxes = [
              meta.group_size && { icon: Users, label: "Group Size", value: meta.group_size },
              meta.dates && { icon: Calendar, label: "Dates", value: meta.dates },
              meta.budget_range && { icon: DollarSign, label: "Budget", value: meta.budget_range },
              meta.objectives?.[0] && { icon: CheckCircle, label: "Goal", value: meta.objectives[0] },
            ].filter(Boolean);
            
            const boxCount = boxes.length;
            
            // Use grid for all counts to ensure equal width boxes
            let gridCols = 'grid-cols-1';
            if (boxCount === 2) {
              gridCols = 'grid-cols-2';
            } else if (boxCount === 3) {
              gridCols = 'grid-cols-3';
            } else if (boxCount === 4) {
              gridCols = 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-4';
            }
            
            return (
              <div className={`grid ${gridCols} gap-4 w-full max-w-6xl px-4 mb-6 sm:mb-20`}>
                {boxes.map((box: any, index: number) => (
                  <DetailBox key={index} icon={box.icon} label={box.label} value={box.value} />
                ))}
              </div>
            );
          })() : (
            // Show empty grid placeholder if no data available
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full max-w-6xl px-4 mb-6 sm:mb-20">
              <DetailBox icon={Users} label="Group Size" value="TBD" />
              <DetailBox icon={Calendar} label="Dates" value="TBD" />
              <DetailBox icon={DollarSign} label="Budget" value="TBD" />
              <DetailBox icon={CheckCircle} label="Goal" value="TBD" />
            </div>
          )}
        </div>

        {/* CTA Button */}
        <button
          onClick={onEnter}
          className={cn(
            "group flex items-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4",
            "text-black font-bold rounded-md",
            "transition-all shadow-lg hover:scale-105",
            "text-sm sm:text-base mb-8 sm:mb-12",
            "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-black"
          )}
          style={{
            backgroundColor: proposalTokens.colors.gold,
            color: '#000000',
            boxShadow: '0 10px 15px -3px rgba(243, 173, 15, 0.2)',
            fontFamily: proposalTokens.typography.fontFamily.sans.join(', '),
            fontWeight: proposalTokens.typography.fontWeight.bold,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(243, 173, 15, 0.9)'; // gold/90
            e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(243, 173, 15, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = proposalTokens.colors.gold;
            e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(243, 173, 15, 0.2)';
          }}
          aria-label="View Proposal"
        >
          View Proposal
          <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
        </button>

        {/* Destinations List */}
        <div className="mb-8 sm:mb-12 w-full max-w-4xl px-2">
          {destinations.length > 0 ? (
            <>
              <p className="text-white/40 text-xs uppercase tracking-wider mb-3 sm:mb-4" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                Featured Destinations
              </p>
              <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
                {destinations.map((dest, index) => (
                  <div
                    key={`${dest.city}-${dest.country}-${index}`}
                    className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/5 border border-white/10 rounded-full backdrop-blur-sm text-xs sm:text-sm"
                    style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}
                  >
                    <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" style={{ color: proposalTokens.colors.gold }} />
                    <span className="text-white font-medium">{dest.city}</span>
                    <span className="text-white/40">{dest.country}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            // Show placeholder if no destinations
            <div className="text-center">
              <p className="text-white/40 text-xs uppercase tracking-wider mb-3 sm:mb-4" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>
                Featured Destinations
              </p>
              <p className="text-white/60 text-sm" style={{ fontFamily: proposalTokens.typography.fontFamily.sans.join(', ') }}>Destinations will appear here</p>
            </div>
          )}
        </div>

        {/* Image Indicators */}
        {images.length > 1 && (
          <div className="fixed bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-20">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setIsTransitioning(true);
                  setTimeout(() => {
                    setCurrentImageIndex(index);
                    setIsTransitioning(false);
                  }, 500);
                }}
                className={cn(
                  "w-2 h-2 rounded-full transition-all",
                  index === currentImageIndex
                    ? 'w-6'
                    : 'bg-white/30 hover:bg-white/50',
                  "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-black"
                )}
                style={{
                  backgroundColor: index === currentImageIndex ? proposalTokens.colors.gold : undefined,
                }}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

